import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ScrollText, Save, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LegalSettingsPanel() {
  const [settings, setSettings] = useState({
    effective_date: '',
    last_updated: '',
    grievance_officer_name: '',
    grievance_officer_email: '',
    grievance_officer_phone: '',
    postal_address: '',
    office_hours: '',
    contact_email: '',
    tips_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/legal`);
        setSettings(data);
      } catch (err) {
        console.error('Error fetching legal settings:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      await axios.put(`${API}/settings/legal`, settings, { withCredentials: true });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save legal settings');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { section: 'Privacy Policy Dates' },
    { name: 'effective_date', label: 'Effective Date', placeholder: 'February 1, 2026' },
    { name: 'last_updated', label: 'Last Updated', placeholder: 'February 1, 2026' },
    { section: 'Grievance Officer (Required under IT Rules)' },
    { name: 'grievance_officer_name', label: 'Officer Name', placeholder: 'e.g. Anil Sharma' },
    { name: 'grievance_officer_email', label: 'Officer Email', placeholder: 'grievance@yourdomain.com' },
    { name: 'grievance_officer_phone', label: 'Officer Phone', placeholder: '+91-XXXXX-XXXXX' },
    { name: 'office_hours', label: 'Office Hours', placeholder: 'Mon to Fri, 10:00 AM to 6:00 PM (IST)' },
    { name: 'postal_address', label: 'Postal Address', placeholder: 'Full Indian office address', textarea: true },
    { section: 'General Contact Emails' },
    { name: 'contact_email', label: 'General Queries Email', placeholder: 'contact@yourdomain.com' },
    { name: 'tips_email', label: 'News Tips Email', placeholder: 'tips@yourdomain.com' }
  ];

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E5E5] p-6" data-testid="legal-settings-loading">
        <div className="text-[#8A8A8A]">Loading legal settings...</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E5E5] p-6" data-testid="legal-settings-panel">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-[#1E3A8A] bg-opacity-10 border border-[#1E3A8A]">
          <ScrollText size={18} className="text-[#1E3A8A]" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-[#1A1A2E]">
          Privacy Policy & Legal
        </h2>
      </div>
      <p className="text-sm text-[#8A8A8A] mb-6">
        These details are displayed on your{' '}
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#E63946] underline">
          Privacy Policy page
        </a>. The Grievance Officer block is <strong>legally required</strong> under the IT Rules, 2011 &amp; 2021.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {fields.map((f, idx) =>
          f.section ? (
            <h3 key={`s-${idx}`} className="text-xs tracking-[0.2em] uppercase font-bold text-[#1A1A2E] pt-3 pb-1 border-b border-[#E5E5E5]">
              {f.section}
            </h3>
          ) : (
            <div key={f.name}>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2">
                {f.label}
              </label>
              {f.textarea ? (
                <textarea
                  name={f.name}
                  value={settings[f.name] || ''}
                  onChange={handleChange}
                  data-testid={`legal-input-${f.name}`}
                  rows="3"
                  placeholder={f.placeholder}
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#1E3A8A] transition-colors resize-none"
                />
              ) : (
                <input
                  type="text"
                  name={f.name}
                  value={settings[f.name] || ''}
                  onChange={handleChange}
                  data-testid={`legal-input-${f.name}`}
                  placeholder={f.placeholder}
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#1E3A8A] transition-colors"
                />
              )}
            </div>
          )
        )}

        {error && (
          <div className="p-3 bg-[#E63946] bg-opacity-10 border border-[#E63946] text-[#E63946] text-sm" data-testid="legal-settings-error">
            {error}
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-500 text-green-700 text-sm" data-testid="legal-settings-success">
            <CheckCircle2 size={16} />
            Privacy Policy details saved successfully.
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          data-testid="legal-settings-save-button"
          className="flex items-center gap-2 px-6 py-3 bg-[#1E3A8A] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#1e40af] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={16} />
          {saving ? 'SAVING...' : 'SAVE LEGAL SETTINGS'}
        </button>
      </form>
    </div>
  );
}

export default LegalSettingsPanel;
